import {
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  Text,
  Button,
  Alert,
  CheckIcon,
  CheckCircleIcon,
  RestoreIcon,
  MailboxIcon,
  RejectIcon,
  RetrieveIcon,
  ActionListItem,
} from '@citibox-panel/ui';
import { isConfirmable } from '../lib/isConfirmable';
import { isRestorable } from '../lib/isRestorable';
import { isNonCollectable } from '../lib/isNonCollectable';
import { isRejectable } from '../lib/isRejectable';
import { isRetrievable } from '../lib/isRetrievable';
import { isNonRetrievable } from '../lib/isNonRetrievable';
import { useReportFailure } from '../../search-shell/hooks/useErrorNotice';
import { enableDeliveryError } from '../lib/enableDeliveryError';
import { AudienceSelect, type ActionAudience } from './AudienceSelect';
import type { ReactNode } from 'react';
import type { DeliveryActionKind, DeliveryActionState } from '../model/deliveryActionFlow';

// Per-action copy for the «Acciones» card. `confirmCta` is the confirm-button label per action (the
// destructive «Rechazar recogida» reads «Sí, rechazar»). Restore's error copy is single-sourced in
// enableDeliveryError (code -> message, incl. its generic); it is the only action with a code map,
// so every other one carries its own generic here.
// // TODO: i18n
// One source for the whole card: only one of the six actions can own the flow at a time, so one
// notice covers them all and a second failure replaces the first.
const NOTICE_ID = 'delivery-action';

const COPY = {
  confirm: {
    listTitle: 'Confirmar recogida',
    listDescription: 'El usuario ya tiene el paquete.',
    title: '¿Confirmar la recogida?',
    body: 'Se marcará la entrega como recogida.',
    confirmCta: 'Sí, confirmar',
    success: 'Recogida confirmada',
    errorGeneric: 'No se pudo confirmar la recogida. Inténtalo de nuevo.',
  },
  restore: {
    listTitle: 'Habilitar recogida',
    listDescription: 'Podrá volver a recogerlo.',
    title: '¿Habilitar la recogida?',
    body: 'Se reabrirá la entrega para volver a recogerla.',
    confirmCta: 'Sí, confirmar',
    success: 'Entrega restaurada',
  },
  nonCollect: {
    listTitle: 'Confirmar recogida vacía',
    listDescription: 'El buzón estaba vacío.',
    title: '¿Confirmar la recogida vacía?',
    body: 'Se marcará la entrega como recogida vacía.',
    confirmCta: 'Sí, confirmar',
    success: 'Recogida vacía confirmada',
    errorGeneric: 'No se pudo confirmar la recogida vacía. Inténtalo de nuevo.',
  },
  reject: {
    listTitle: 'Rechazar recogida',
    listDescription: 'El usuario no quiere el paquete.',
    title: '¿Rechazar la recogida?',
    body: 'Se marcará la entrega como rechazada por el usuario.',
    confirmCta: 'Sí, rechazar',
    success: 'Recogida rechazada',
    errorGeneric: 'No se pudo rechazar la recogida. Inténtalo de nuevo.',
  },
  retrieve: {
    listTitle: 'Recuperado',
    listDescription: 'El mensajero ha recuperado el paquete.',
    title: '¿Confirmar la recuperación?',
    body: 'El mensajero se ha llevado el paquete.',
    confirmCta: 'Sí, confirmar',
    success: 'Recuperación confirmada',
    errorGeneric: 'No se pudo confirmar la recuperación. Inténtalo de nuevo.',
  },
  nonRetrieve: {
    listTitle: 'Recuperación vacía',
    listDescription: 'El mensajero encontró el buzón vacío.',
    title: '¿Confirmar la recuperación vacía?',
    body: 'Se marcará la entrega como recuperación vacía.',
    confirmCta: 'Sí, confirmar',
    success: 'Recuperación vacía confirmada',
    errorGeneric: 'No se pudo confirmar la recuperación vacía. Inténtalo de nuevo.',
  },
} as const;

// One sentence per audience: the shared one was false under «Mensajero» — on a collected delivery
// it claimed no action existed «for this state» while «Usuario» still had eligible ones a click
// away. The audience decides which sentence, never which actions.
// TODO: i18n
const NO_ELIGIBLE_ACTIONS_COPY: Record<ActionAudience, string> = {
  user: 'No hay acciones disponibles para este estado.',
  courier: 'No hay acciones de mensajero para este estado.',
};

// The card paints six of the nine action kinds, split by audience. The others run their own
// instance of the shared reducer elsewhere on screen — «Ampliar custodia» in the Trazabilidad,
// «Confirmar secuencia» on a carril — and never reach this one. Membership is COPY itself, so the
// set the card paints and the set it has words for cannot drift: an exclude list would let the next
// foreign kind through into a lookup that has no entry for it.
const isCardAction = (kind: DeliveryActionKind | null): kind is keyof typeof COPY =>
  kind !== null && Object.hasOwn(COPY, kind);

export interface DeliveryActionsCardProps {
  status: string;
  // An OPAQUE node mounted below the actions, behind a full-bleed rule, inside this same card. The
  // card learns nothing about what is in it — the same deal `DeliveryDetailCard` has with the
  // «Ampliar custodia» control — so whatever the detail wants to hang here needs no change in here.
  sharingSection?: ReactNode;
  state: DeliveryActionState;
  // Controlled from DeliveryLayout, like `state` and for the same reason: the success refetch
  // unmounts this card behind the skeleton, and the active audience must survive that remount.
  audience: ActionAudience;
  onAudienceChange: (audience: ActionAudience) => void;
  onStart: (action: DeliveryActionKind) => void;
  onCancel: () => void;
  onConfirm: () => void;
  // Fired once a failure has been reported to the shell's notice, so DeliveryLayout can put the
  // flow back to idle: the action rows come back while the notice stays up until dismissed.
  onFailureReported: () => void;
}

// The «Acciones» card: six actions split by the header's audience selector — «Usuario» paints its
// four rows, «Mensajero» paints «Recuperado» then «Recuperación vacía» — driven by `state` (owned
// by DeliveryLayout, so it survives the success-refetch skeleton remount). The header always shows;
// below it the state's content — the eligible action(s) of the active audience, the inline
// confirm/cancel panel for the action in flow (mutual exclusion over the WHOLE card: both
// audiences share the one slot), or a success/error banner, which being phase-gated survives an
// audience switch. Case-specific restore errors persist (a close «X» → onDismiss); the generic
// error / success auto-dismiss via DeliveryLayout's timer.
// flex 1 — a third of the row.
// «Ampliar custodia» is deliberately NOT here: it hangs off the «Expira la recogida» milestone in
// the Trazabilidad, next to the date it moves (SPEC D1, reversed 2026-08-07). It runs its own
// instance of this same reducer, which is why this card's own flow is untouched by it.
export function DeliveryActionsCard({
  status,
  sharingSection,
  state,
  audience,
  onAudienceChange,
  onStart,
  onCancel,
  onConfirm,
  onFailureReported,
}: DeliveryActionsCardProps) {
  const { phase, errorCode } = state;
  // Narrowing here — rather than widening COPY with dead entries — is what keeps the lookups below
  // total over the six the card actually paints.
  const action = isCardAction(state.action) ? state.action : null;

  // Resolve the failure's copy once: restore reads its code-specific map (fail-open); every other
  // action has no code map, so it always reports its own generic. Never the backend string, and
  // never the code either.
  const failure =
    action !== null && phase === 'error'
      ? action === 'restore'
        ? enableDeliveryError(errorCode)
        : COPY[action].errorGeneric
      : null;
  // Floating, and the card keeps nothing (ADR 0021): reporting hands the flow back, so the rows
  // the agent has to press again are on screen instead of a banner they cannot act on. It is also
  // what makes the failure survive this card's own remount — the success refetch unmounts it, and
  // the notice lives in the shell.
  useReportFailure(NOTICE_ID, failure, onFailureReported);

  // The PHASE GUARD LIVES IN HERE, exactly like the `error` resolution above — that is what lets the
  // JSX mount on `success !== null` alone. Resolving the copy without the phase and guarding at the
  // mount site would put the same condition in two places, and the copy is non-null for all six
  // actions the moment one owns the flow, so the green banner would flash over the confirm panel
  // while the agent is still reading it.
  const success = action !== null && phase === 'success' ? COPY[action].success : null;

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, flex: 1, minWidth: 0 }}>
      <CardContent>
        <Stack spacing={1}>
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            {/* TODO: i18n — copy en ES hasta Fase 1. */}
            <Text variant="body1" component="h3" bold>
              Acciones
            </Text>
            <AudienceSelect
              value={audience}
              onChange={onAudienceChange}
              disabled={phase === 'pending'}
            />
          </Stack>

          {phase === 'idle' && audience === 'user' && (
            <>
              {/* Order is a rule, not the order these shipped in: ascending consequence, destructive
                  last. «Habilitar recogida» is the only one that re-opens a pickup, so it leads
                  wherever it is eligible — agents click the first row from habit, and where both are
                  offered that habit used to land on «Confirmar recogida». */}
              {isRestorable(status) && (
                <ActionListItem
                  icon={<RestoreIcon />}
                  title={COPY.restore.listTitle}
                  description={COPY.restore.listDescription}
                  onClick={() => {
                    onStart('restore');
                  }}
                />
              )}
              {isConfirmable(status) && (
                <ActionListItem
                  icon={<CheckCircleIcon />}
                  title={COPY.confirm.listTitle}
                  description={COPY.confirm.listDescription}
                  onClick={() => {
                    onStart('confirm');
                  }}
                />
              )}
              {isNonCollectable(status) && (
                <ActionListItem
                  icon={<MailboxIcon />}
                  title={COPY.nonCollect.listTitle}
                  description={COPY.nonCollect.listDescription}
                  onClick={() => {
                    onStart('nonCollect');
                  }}
                />
              )}
              {isRejectable(status) && (
                <ActionListItem
                  tone="danger"
                  icon={<RejectIcon />}
                  title={COPY.reject.listTitle}
                  description={COPY.reject.listDescription}
                  onClick={() => {
                    onStart('reject');
                  }}
                />
              )}
              {!isConfirmable(status) &&
                !isRestorable(status) &&
                !isNonCollectable(status) &&
                !isRejectable(status) && (
                  <Text variant="body2" color="secondary">
                    {NO_ELIGIBLE_ACTIONS_COPY[audience]}
                  </Text>
                )}
            </>
          )}

          {phase === 'idle' && audience === 'courier' && (
            <>
              {/* Same rule as the «Usuario» list: ascending consequence. Completing the retrieval
                  is the happy case and leads; registering an empty box implies a missing parcel. */}
              {isRetrievable(status) && (
                // Default tone on purpose: the retrieval completes the case, it destroys nothing.
                <ActionListItem
                  icon={<RetrieveIcon />}
                  title={COPY.retrieve.listTitle}
                  description={COPY.retrieve.listDescription}
                  onClick={() => {
                    onStart('retrieve');
                  }}
                />
              )}
              {/* Default tone too: registering reality is not destructive. Same glyph as «Confirmar
                  recogida vacía» on purpose — same physical fact (open box, empty), and the two
                  audiences never paint at once. */}
              {isNonRetrievable(status) && (
                <ActionListItem
                  icon={<MailboxIcon />}
                  title={COPY.nonRetrieve.listTitle}
                  description={COPY.nonRetrieve.listDescription}
                  onClick={() => {
                    onStart('nonRetrieve');
                  }}
                />
              )}
              {!isRetrievable(status) && !isNonRetrievable(status) && (
                <Text variant="body2" color="secondary">
                  {NO_ELIGIBLE_ACTIONS_COPY[audience]}
                </Text>
              )}
            </>
          )}

          {action !== null && (phase === 'confirming' || phase === 'pending') && (
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.default',
                p: 2,
              }}
            >
              <Stack spacing={1}>
                <Text variant="subtitle1" component="p" bold>
                  {COPY[action].title}
                </Text>
                <Text variant="body2" color="secondary">
                  {COPY[action].body}
                </Text>
                <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                  <Button variant="text" onClick={onCancel} disabled={phase === 'pending'}>
                    Cancelar
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<CheckIcon />}
                    loading={phase === 'pending'}
                    onClick={onConfirm}
                  >
                    {COPY[action].confirmCta}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}

          {/* Success stays IN PLACE: ADR 0021 is about errors, and a confirmation belongs in the
              card whose row was pressed. */}
          {success !== null && <Alert severity="success">{success}</Alert>}
        </Stack>
      </CardContent>
      {/* Outside the CardContent on purpose: the rule has to reach both edges, and CardContent's
          padding would leave it short of them. One card with two sections, not two cards. */}
      {sharingSection !== undefined && (
        <>
          <Divider />
          <CardContent>{sharingSection}</CardContent>
        </>
      )}
    </Card>
  );
}
